import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";

import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import { connect } from "react-redux";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import { Tooltip } from "@mui/material";
import OwnField from "./ModalField";
import { defaultPasscodeTheme } from "../../../../../constants/passCodeUtils";
import { toggleInfoModal, closeTicket, setOrderId } from "../../../../../actions/projectmail";

export const InfoModalComponent = ({ infoTicket, open, close, closeTicketFn, translate, setOrderIdFn }) => {
  const [ownTicket, setOwnTicket] = useState(infoTicket);
  const [openModal, setOpenModal] = useState(false);
  const [newOrderID, setNewOrderID] = useState(infoTicket ? infoTicket.orderID : "");
  const [disableButton, setDisableButton] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleClose = () => {
    close();
  };

  useEffect(() => {
    if (infoTicket !== ownTicket && infoTicket) {
      setOwnTicket(infoTicket);
      if (infoTicket.orderID !== -1) setNewOrderID(infoTicket.orderID);
    }
  }, [infoTicket, ownTicket, setOwnTicket]);

  const theme = useTheme();
  const pc = useMemo(() => defaultPasscodeTheme(theme), [theme]);
  const s = useMemo(
    () => ({
      modal: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      paper: {
        width: "750px",
        backgroundColor: theme.palette.background.paper,
        border: "2px solid #000",
        boxShadow: theme.shadows[5],
        p: theme.spacing(2, 4, 3),
        maxHeight: "927px",
        overflowY: "auto",
      },
      closePaper: {
        width: "650px",
        backgroundColor: theme.palette.background.paper,
        border: "2px solid #000",
        boxShadow: theme.shadows[5],
        p: theme.spacing(2, 4, 3),
      },
      paddingTop: {
        pt: 2,
      },
      marginTop: {
        mt: -1,
      },
      flexGrow: {
        flexGrow: 1,
      },
      header: {
        fontWeight: "bold",
        fontSize: "16px",
        width: "100%",
        textAlign: "center",
      },
      orderField: {
        width: "100%",
        m: 1,
      },
      button: {
        p: 1,
        pt: 2,
      },
      center: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
    }),
    [theme]
  );
  return (open === true &&
  ownTicket !== undefined && [
    <Modal key="inner-info-modal" open={open} sx={s.modal} onClose={handleClose}>
      <Fade in={open}>
        <Box sx={s.paper}>
          <Box sx={s.paddingTop} />
          <Box sx={s.header}>{translate.ticketData}</Box>
          <Box sx={s.paddingTop} />
          <Paper elevation={6}>
            <Box sx={pc.inline}>
              <OwnField translationKey="EmailTicketSystemOwner" value={ownTicket.owner} />
              <OwnField translationKey="Priority" value={ownTicket.priority} />
            </Box>
            <Box sx={pc.lineBreak} />
            <Box sx={pc.inline}>
              <OwnField translationKey="firstName" value={ownTicket.customerFirstName} />
              <OwnField translationKey="lastName" value={ownTicket.customerLastName} />
            </Box>
            <Box sx={pc.lineBreak} />
            <Box sx={pc.inline}>
              <OwnField translationKey="customer" value={ownTicket.customer} />
              <OwnField translationKey="externalId" value={ownTicket.externalID} />
            </Box>
            <Box sx={pc.lineBreak} />
            <Box sx={pc.inline}>
              <OwnField translationKey="lastUpdateTime" value={ownTicket.lastUpdatedAt} date />
            </Box>
            <Box sx={pc.lineBreak} />
          </Paper>
          <Box sx={s.paddingTop} />
          <Box sx={s.header}>{translate.home2Data}</Box>
          <Box sx={s.paddingTop} />
          <Paper elevation={6}>
            <Box sx={pc.lineBreak} />
            <Box sx={pc.inline}>
              <OwnField translationKey="operator" value={ownTicket.operator} />
              <OwnField translationKey="additionalOperator" value={ownTicket.additionalOperator} />
            </Box>
            <Box sx={pc.lineBreak} />
            <Box sx={pc.inline}>
              <OwnField translationKey="eventStartTime" value={ownTicket.eventStartTime} date />
            </Box>
            <Box sx={pc.inline}>
              <OwnField translationKey="eventEndTime" value={ownTicket.eventEndTime} date />
            </Box>
            <Box sx={pc.lineBreak} />
            <Box sx={pc.inline}>
              <OwnField translationKey="orderProduct" value={ownTicket.orderProduct} />
              <OwnField translationKey="orderStatus" value={ownTicket.orderStatus} />
            </Box>
            <Box sx={pc.lineBreak} />
            <Box sx={pc.inline}>
              <OwnField translationKey="internalNotes" value={ownTicket.notes} note />
            </Box>
            <Box sx={pc.lineBreak} />
            <Box sx={pc.inline}>
              <OwnField translationKey="externalNotes" value={ownTicket.externalNotes} note />
            </Box>
            <Box sx={pc.lineBreak} />
            <Box sx={pc.inline}>
              <TextField
                id="order-id-inputField"
                label={translate.setOrderId}
                sx={[
                  s.orderField,
                  {
                    "& input:invalid": {
                      background: "#F08080",
                    },
                  },
                ]}
                padding="normal"
                value={newOrderID || ""}
                onChange={e => {
                  setNewOrderID(e.target.value);
                  setSaved(false);
                  const regex = new RegExp("^[a-zA-Z0-9]{0,8}$");
                  setDisableButton(!regex.test(e.target.value));
                }}
                slotProps={{
                  htmlInput: { pattern: "^[a-zA-Z0-9]{0,8}$" },
                }}
              />
              <Box sx={s.button}>
                <Button
                  onClick={async () => {
                    setSaved(false);
                    setDisableButton(true);
                    await setOrderIdFn(ownTicket.id, newOrderID);
                    setDisableButton(false);
                    setSaved(true);
                  }}
                  variant="contained"
                  color="primary"
                  disabled={disableButton}
                >
                  {translate.saveButton}
                </Button>
                {saved && <Box sx={s.center}>{translate.saved}!</Box>}
              </Box>
              <Box sx={[s.button, s.marginTop]}>
                <Tooltip title={translate.deleteButton} enterDelay={500} placement="top">
                  <IconButton
                    color="secondary"
                    disabled={disableButton}
                    onClick={async () => {
                      setSaved(false);
                      setDisableButton(true);
                      setNewOrderID("");
                      await setOrderIdFn(ownTicket.id, -1);
                      setDisableButton(false);
                      setSaved(true);
                    }}
                    size="large">
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Paper>
          <Box sx={s.paddingTop}>
            <Box sx={pc.inline}>
              <Button onClick={() => setOpenModal(true)} variant="contained" color="secondary">
                {translate.closeTicket}
              </Button>
              <Box sx={s.flexGrow} />
              <Button variant="contained" onClick={() => close()}>
                {translate.cancel}
              </Button>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>,
    <Modal key="close-Modal" open={openModal} sx={s.modal} onClose={() => setOpenModal(false)}>
      <Fade in={openModal}>
        <Box sx={s.closePaper}>
          <Box>{translate.closeTicketModal}</Box>
          <Box sx={s.paddingTop} />
          <Box sx={pc.inline}>
            <Button
              onClick={() => {
                closeTicketFn(ownTicket.id);
                close();
                setOpenModal(false);
              }}
              variant="contained"
              color="secondary"
            >
              {translate.yes}
            </Button>
            <Box sx={s.flexGrow} />
            <Button
              variant="contained"
              onClick={() => {
                close();
                setOpenModal(false);
              }}
            >
              {translate.cancel}
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>,
  ]);
};

InfoModalComponent.propTypes = {
  translate: PropTypes.object.isRequired,
  close: PropTypes.func.isRequired,
  setOrderIdFn: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

const mapDispatchToProps = {
  close: () => dispatch => dispatch(toggleInfoModal(false)),
  closeTicketFn: id => dispatch => dispatch(closeTicket(id)),
  setOrderIdFn: (ticketId, orderId) => async dispatch => {
    await dispatch(setOrderId(orderId, ticketId));
  },
};

InfoModalComponent.defaultProps = {
  infoTicket: undefined,
};

const mapStateToProps = state => ({
  open: state.projectmail.infoModalOpen,
  infoTicket: state.projectmail.currentRow,
  translate: state.ui.translate,
});

const InfoModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(InfoModalComponent);

export default InfoModal;
